// ---------------------------------------------------------
// Datei: AllgemeineUploads.tsx
// Zweck: Projektbezogene Ablage für Schemen, Skizzen und weitere Baustellendokumente.
// Kontext: Die Dateien liegen im Google-Drive-Projektordner unter Dokumente/Baustellendoku/Zusatz
//          (derselbe Ordner wie die übrigen Checklisten-Fotos der Durchführungs-Phase, nur eine
//          Ebene tiefer) und werden über One360 geladen.
// Stand: 2026-08-14
// ---------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '../contexts/ToastContext';
import {
  getConstructionDocumentationFilesAsync,
  getFileAsync,
  getThumbnailAsync,
  uploadConstructionDocumentationFileAsync,
} from '../services/api/drive';
import { isPhotoFile, normalizePhotoFile } from '../services/imageService';
import { describeConstructionDocumentationLoadError } from '../services/constructionDocumentationErrors';
import DocumentPreviewModal from './DocumentPreviewModal';
import type { ConstructionDocumentationFile } from '../interfaces/drive';

// Freie Uploads aus diesem Formular landen serverseitig im Unterordner "Zusatz" innerhalb von
// Dokumente/Baustellendoku (siehe One360-API-Erweiterung, docs/One360-API-Anforderung-
// Baustellendokumentation-Upload.md) — demselben Ordner, den auch alle anderen Checklisten-Fotos
// der Durchführungs-Phase nutzen (z.B. Außenfühler, Außeneinheit + Fundament), nur eine Ebene
// tiefer. Liste und Upload werden dadurch beide serverseitig auf diesen Unterordner beschränkt,
// keine clientseitige Namens-Filterung mehr nötig.
const ZUSATZ_SUBFOLDER = 'Zusatz';

interface AllgemeineUploadsProps {
  dealId: string;
  dealName: string;
}

interface PendingFile {
  id: string;
  name: string;
  type: string;
  size: number;
  dataUrl: string;
  file: File;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const ALLOWED_DOCUMENT_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];

const isAllowedFile = (file: File): boolean =>
  isPhotoFile(file) || ALLOWED_DOCUMENT_TYPES.includes(file.type);

const readFileDataUrl = (file: File): Promise<string> => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result as string);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Kategorie wird direkt in den Dateinamen kodiert (Nutzer-Rückmeldung: "kann man ja aus dem
// Dateinamen filtern") — kein eigenes Backend-Feld nötig. " - " als Trenner, weil er auch beim
// direkten Browsen in Google Drive gut lesbar bleibt (z.B. "Wanddurchbruch - a1b2c3d4.jpg"). Der
// ursprüngliche Dateiname wird bewusst nicht übernommen (Nutzer-Rückmeldung: Kategorie + kurze
// UUID reicht) — Kamera-Dateinamen wie "IMG_20260814.jpg" sind ohnehin nicht aussagekräftig.
const CATEGORY_SEPARATOR = ' - ';
const UNCATEGORIZED_LABEL = 'Ohne Kategorie';
// Sentinel-Wert im thumbnails-Cache: unterscheidet "noch am Laden" (kein Eintrag) von
// "Laden fehlgeschlagen" (dieser Wert) — sonst bleibt die Kachel bei einem Fehler für immer im
// Pulsier-Platzhalter hängen, ohne dass sichtbar wird, dass etwas schiefgelaufen ist
// (PR-Review-Finding; getThumbnailAsync loggt den eigentlichen Fehler bereits selbst).
const THUMBNAIL_LOAD_FAILED = '__failed__';

const sanitizeCategory = (category: string): string =>
  category
    .trim()
    .replace(/[/\\:*?"<>|]/g, '-')
    .replace(/\s*-\s*/g, '-') // Bindestriche im Kategorienamen selbst dürfen nicht wie der Trenner aussehen
    .replace(/\s+/g, ' ')
    .trim();

const getFileExtension = (fileName: string): string => {
  const match = fileName.match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : 'dat';
};

const buildFileName = (category: string, originalName: string): string => {
  const extension = getFileExtension(originalName);
  const suffix = uuidv4().slice(0, 8);
  const sanitized = sanitizeCategory(category);
  return sanitized ? `${sanitized}${CATEGORY_SEPARATOR}${suffix}.${extension}` : `${suffix}.${extension}`;
};

/** Zerlegt einen Dateinamen zurück in Kategorie (falls vorhanden) und restlichen Namen. */
const parseFileName = (name: string): { category: string | null; label: string } => {
  const separatorIndex = name.indexOf(CATEGORY_SEPARATOR);
  if (separatorIndex <= 0) return { category: null, label: name };
  return { category: name.slice(0, separatorIndex), label: name.slice(separatorIndex + CATEGORY_SEPARATOR.length) };
};

const AllgemeineUploads: React.FC<AllgemeineUploadsProps> = ({ dealId, dealName }) => {
  const { showToast } = useToast();
  const [kategorie, setKategorie] = useState('');
  const [notiz, setNotiz] = useState('');
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  const [files, setFiles] = useState<ConstructionDocumentationFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});
  const [previewFile, setPreviewFile] = useState<ConstructionDocumentationFile | null>(null);
  const [previewData, setPreviewData] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // PR-Review-Finding: schützt gegen Race Conditions, wenn schnell hintereinander mehrere
  // Vorschauen geöffnet werden — nur die zuletzt angeforderte Antwort darf noch UI-State setzen.
  const previewRequestIdRef = useRef(0);
  // Nur für die Fehlermeldung nötig – als Ref, damit ein später gesetzter Name kein Neuladen auslöst.
  const dealNameRef = useRef(dealName);
  dealNameRef.current = dealName;

  // Gruppiert die bereits hochgeladenen Dateien nach der aus dem Dateinamen geparsten Kategorie
  // (Nutzer-Rückmeldung: Liste soll nach Kategorie sortiert/gruppiert sein).
  const groupedFiles = useMemo(() => {
    const groups = new Map<string, ConstructionDocumentationFile[]>();
    for (const file of files) {
      const { category } = parseFileName(file.name);
      const key = category ?? UNCATEGORIZED_LABEL;
      const group = groups.get(key);
      if (group) {
        group.push(file);
      } else {
        groups.set(key, [file]);
      }
    }
    return Array.from(groups.entries()).sort(([a], [b]) => {
      if (a === UNCATEGORIZED_LABEL) return 1;
      if (b === UNCATEGORIZED_LABEL) return -1;
      return a.localeCompare(b, 'de-DE');
    });
  }, [files]);

  const loadFiles = useCallback(async () => {
    if (!dealId) return;
    setLoadingFiles(true);
    try {
      const response = await getConstructionDocumentationFilesAsync(dealId, ZUSATZ_SUBFOLDER);
      setFiles(response.files);
    } catch (error) {
      console.error('[Baustellendokumentation] Google-Drive-Dateien konnten nicht geladen werden:', error);
      // AB#1474: konkrete Ursache + Projekt-Referenz statt festem Text; Support-Texte brauchen
      // mehr Lesezeit als die üblichen 4 s.
      const message = describeConstructionDocumentationLoadError(error, { dealId, dealName: dealNameRef.current });
      if (message) {
        showToast('error', message, 8000);
      }
    } finally {
      setLoadingFiles(false);
    }
  }, [dealId, showToast]);

  useEffect(() => {
    void loadFiles();
  }, [loadFiles]);

  // Thumbnails für Bilddateien nachladen (Drive-thumbnailLink ist nur mit dem Auth-Kontext des
  // Service-Accounts gültig, siehe getThumbnailAsync-Doku in services/api/drive.ts) — dieselbe
  // Vorschau, die auch die restliche App für hochgeladene Fotos verwendet.
  useEffect(() => {
    let cancelled = false;
    const missing = files.filter((file) => file.mimeType.startsWith('image/') && !thumbnails[file.id]);
    if (missing.length === 0) return;

    missing.forEach((file) => {
      void getThumbnailAsync(file.id, file.thumbnailLink).then((content) => {
        if (cancelled) return;
        setThumbnails((previous) => (previous[file.id] ? previous : {
          ...previous,
          [file.id]: content ? `data:${content.mimeType};base64,${content.base64Content}` : THUMBNAIL_LOAD_FAILED,
        }));
      });
    });

    return () => {
      cancelled = true;
    };
    // thumbnails bewusst nicht in den Deps: der `missing`-Check liest den zum Render-Zeitpunkt
    // aktuellen Cache aus der Closure, exakt wie im analogen Thumbnail-Effect in
    // DocumentChecklist.tsx — ein Re-Run pro Cache-Update wäre unnötig, da files der
    // eigentliche Auslöser für neue Kandidaten ist.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files]);

  // Volle Vorschau (nicht nur Thumbnail) beim Öffnen nachladen — inkl. Notiz als Beschreibung
  // (Nutzer-Rückmeldung: Notiz soll als Bild-Beschreibung im Preview sichtbar sein).
  const openPreview = async (file: ConstructionDocumentationFile) => {
    const requestId = ++previewRequestIdRef.current;
    setPreviewFile(file);
    setPreviewLoading(true);
    try {
      const content = await getFileAsync(file.id);
      // Falls währenddessen bereits eine neuere Vorschau angefordert wurde: dieses (jetzt
      // veraltete) Ergebnis verwerfen, statt es über die inzwischen für eine andere Datei
      // geöffnete Vorschau zu legen.
      if (previewRequestIdRef.current !== requestId) return;
      setPreviewData(content ? `data:${content.mimeType};base64,${content.base64Content}` : '');
    } finally {
      if (previewRequestIdRef.current === requestId) {
        setPreviewLoading(false);
      }
    }
  };

  const closePreview = () => {
    setPreviewFile(null);
    setPreviewData('');
  };

  // PR-Review-Finding: ein Abgleich gegen bereits hochgeladene `files` ist seit `buildFileName`
  // sinnlos geworden — jede gesendete Datei bekommt beim Senden immer einen frischen
  // "<Kategorie> - <uuid>"-Namen, der nie mit einem Original-Dateinamen wie "Schema.pdf"
  // übereinstimmt. Die Prüfung schützt deshalb bewusst nur noch davor, dieselbe Datei innerhalb
  // desselben, noch nicht gesendeten Batches versehentlich doppelt hinzuzufügen.
  const addPendingFiles = (newPendingFiles: PendingFile[]) => {
    const knownNames = new Set(
      pendingFiles.map((file) => file.name.trim().toLocaleLowerCase('de-DE')),
    );
    const accepted = newPendingFiles.filter((file) => {
      const name = file.name.trim().toLocaleLowerCase('de-DE');
      if (knownNames.has(name)) return false;
      knownNames.add(name);
      return true;
    });
    if (accepted.length !== newPendingFiles.length) {
      showToast('error', 'Diese Datei(en) wurden bereits zum Senden vorgemerkt.', 4000);
    }
    setPendingFiles((previous) => [...previous, ...accepted]);
  };

  // Direkte native Dateiauswahl statt eines eigenen Auswahl-Dialogs (Nutzer-Rückmeldung, Ticket
  // #1126-Folgearbeit): der bisherige FileUploadModal-Zwischenschritt hatte selbst einen
  // "Hochladen"-Button, der nur die Auswahl bestätigte statt tatsächlich hochzuladen — das wirkte
  // wie ein erzwungenes Sofort-Senden, bevor überhaupt eine Notiz geschrieben werden konnte. Der
  // native Datei-Picker (ohne "capture") bietet auf Mobilgeräten Kamera, Galerie und Dateisystem
  // gemeinsam an; die echte Vorschau + Notiz + Senden-Schritt bleiben unverändert unten.
  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = event.target.files;
    if (!selectedFiles || selectedFiles.length === 0) return;

    const newPendingFiles: PendingFile[] = [];
    for (const selectedFile of Array.from(selectedFiles)) {
      if (selectedFile.size > MAX_FILE_SIZE) {
        showToast('error', `Datei „${selectedFile.name}“ ist zu groß (max. 20 MB).`, 4000);
        continue;
      }
      if (!isAllowedFile(selectedFile)) {
        showToast('error', `Dateityp von „${selectedFile.name}“ wird nicht unterstützt.`, 4000);
        continue;
      }

      try {
        if (isPhotoFile(selectedFile)) {
          const normalized = await normalizePhotoFile(selectedFile);
          newPendingFiles.push({
            id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            name: normalized.fileName,
            type: normalized.contentType,
            size: normalized.size,
            dataUrl: normalized.dataUrl,
            file: normalized.file,
          });
        } else {
          newPendingFiles.push({
            id: `pending-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            name: selectedFile.name,
            type: selectedFile.type,
            size: selectedFile.size,
            dataUrl: await readFileDataUrl(selectedFile),
            file: selectedFile,
          });
        }
      } catch (error) {
        console.error('[Baustellendokumentation] Datei konnte nicht verarbeitet werden:', error);
        showToast('error', `Datei „${selectedFile.name}“ konnte nicht verarbeitet werden.`, 4000);
      }
    }

    if (newPendingFiles.length > 0) {
      addPendingFiles(newPendingFiles);
    }
    // Input zurücksetzen, damit dieselbe Datei erneut auswählbar bleibt (z.B. nach "Entfernen").
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUpload = async () => {
    if (!pendingFiles.length || uploading) return;

    const selectedFiles = [...pendingFiles];
    // Notiz gilt für den ganzen Batch und wird direkt als Drive-Beschreibung jeder Datei
    // gesetzt (Nutzer-Rückmeldung) — keine separate .txt-Datei mehr.
    const description = notiz.trim() || undefined;
    // Kategorie wird beim Senden (nicht schon bei der Auswahl) in den Dateinamen eingebaut, damit
    // sie sich für den ganzen Batch noch ändern lässt, bevor tatsächlich hochgeladen wird.
    const category = kategorie.trim();
    setUploading(true);
    try {
      // Der Drive-Ordner wird pro Datei serverseitig aufgelöst. Sequenziell verhindert
      // zudem doppelte Namen, wenn Mobilgeräte denselben Upload mehrfach auslösen.
      for (const pendingFile of selectedFiles) {
        const uploadFile = new File(
          [pendingFile.file],
          buildFileName(category, pendingFile.file.name),
          { type: pendingFile.file.type },
        );
        await uploadConstructionDocumentationFileAsync(dealId, dealName, uploadFile, {
          subfolder: ZUSATZ_SUBFOLDER,
          description,
        });
        setPendingFiles((previous) => previous.filter((file) => file.id !== pendingFile.id));
      }
      setNotiz('');
      setKategorie('');
      await loadFiles();
      showToast('success', `${selectedFiles.length} Datei(en) in Google Drive abgelegt`, 3000);
    } catch (error) {
      console.error('[Baustellendokumentation] Google-Drive-Upload fehlgeschlagen:', error);
      showToast('error', error instanceof Error ? error.message : 'Upload fehlgeschlagen', 4000);
      await loadFiles();
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-700 dark:bg-blue-900/20">
        <p className="text-sm text-blue-700 dark:text-blue-300">
          Alle Dateien werden im Google-Drive-Projektordner unter „Dokumente &gt; Baustellendoku &gt; Zusatz“ gespeichert.
        </p>
      </div>

      <div className="space-y-4 rounded-lg bg-white p-4 shadow dark:bg-gray-800">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Neue Baustellendokumente</h2>
        <input
          type="text"
          value={kategorie}
          onChange={(event) => setKategorie(event.target.value)}
          placeholder="Kategorie (z.B. Wanddurchbruch) — wird zum Dateinamen"
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        />
        <textarea
          value={notiz}
          onChange={(event) => setNotiz(event.target.value)}
          placeholder="Optionale Notiz zum Upload"
          rows={2}
          className="w-full resize-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        />

        {/* Zwei getrennte native Inputs statt einem gemeinsamen (Nutzer-Rückmeldung: Kamera fehlte) —
            manche mobilen Browser bieten den Kamera-Eintrag im Auswahldialog nicht mehr an, sobald
            `accept` auch Nicht-Bild-Typen wie PDF enthält. Ein reiner `image/*`-Input mit
            `capture="environment"` erzwingt/bevorzugt zuverlässig die Kamera. */}
        <div className="flex gap-2">
          <label className="flex flex-1 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-3 py-3 text-center text-sm text-gray-700 dark:border-gray-600 dark:text-gray-200">
            Foto aufnehmen
            <input
              data-testid="allgemeine-uploads-camera-input"
              type="file"
              accept="image/*"
              capture="environment"
              disabled={uploading}
              onChange={(event) => void handleFileInputChange(event)}
              className="sr-only"
            />
          </label>
          <label className="flex flex-1 cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-3 py-3 text-center text-sm text-gray-700 dark:border-gray-600 dark:text-gray-200">
            Datei auswählen
            <input
              ref={fileInputRef}
              data-testid="allgemeine-uploads-file-input"
              type="file"
              accept="image/*,application/pdf"
              multiple
              disabled={uploading}
              onChange={(event) => void handleFileInputChange(event)}
              className="sr-only"
            />
          </label>
        </div>

        {pendingFiles.length > 0 && (
          <ul className="space-y-2">
            {pendingFiles.map((file) => (
              <li key={file.id} className="flex items-center gap-3 rounded border px-3 py-2 text-sm dark:border-gray-600 dark:text-white">
                <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100 dark:bg-gray-700">
                  {file.type.startsWith('image/') ? (
                    <img src={file.dataUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <svg className="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                </div>
                <span className="min-w-0 flex-1 truncate">{file.name}</span>
                <button type="button" onClick={() => setPendingFiles((previous) => previous.filter((entry) => entry.id !== file.id))} className="flex-shrink-0 text-red-600">
                  Entfernen
                </button>
              </li>
            ))}
          </ul>
        )}

        {pendingFiles.length > 0 && (
          <button type="button" onClick={() => void handleUpload()} disabled={uploading} className="w-full rounded-lg bg-primary-600 px-4 py-3 font-medium text-white disabled:opacity-50">
            {uploading ? 'Wird gesendet …' : `${pendingFiles.length} Datei(en) senden`}
          </button>
        )}
      </div>

      <div className="rounded-lg bg-white p-4 shadow dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">Baustellendokumentation</h2>
        {loadingFiles ? <p className="text-sm text-gray-500">Dokumente werden geladen …</p> :
          files.length === 0 ? <p className="text-sm text-gray-500">Noch keine Dokumente abgelegt.</p> :
            <div className="space-y-5">
              {groupedFiles.map(([category, groupFiles]) => (
                <div key={category}>
                  <h3 className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">{category}</h3>
                  <div className="flex flex-wrap gap-3">
                    {groupFiles.map((file) => {
                      const { label } = parseFileName(file.name);
                      const thumbnail = thumbnails[file.id];
                      const thumbnailFailed = thumbnail === THUMBNAIL_LOAD_FAILED;
                      return (
                        <button
                          key={file.id}
                          type="button"
                          onClick={() => void openPreview(file)}
                          aria-label={thumbnailFailed ? `${label} (Vorschau nicht verfügbar)` : label}
                          title={thumbnailFailed ? `${label} (Vorschau nicht verfügbar)` : label}
                          className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-700"
                        >
                          {file.mimeType.startsWith('image/') && !thumbnailFailed ? (
                            thumbnail ? (
                              <img src={thumbnail} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <div className="h-full w-full animate-pulse bg-gray-200 dark:bg-gray-600" />
                            )
                          ) : (
                            <div className="flex h-full w-full items-center justify-center">
                              <svg className={`h-8 w-8 ${thumbnailFailed ? 'text-red-400' : 'text-gray-400'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>}
      </div>

      {previewFile && previewLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-white border-t-transparent" />
        </div>
      )}

      {previewFile && !previewLoading && (
        <DocumentPreviewModal
          isOpen
          onClose={closePreview}
          documentName={previewFile.name}
          documentType={previewFile.mimeType.startsWith('image/') ? 'image' : 'pdf'}
          previewData={previewData}
          caption={previewFile.description}
        />
      )}
    </div>
  );
};

export default AllgemeineUploads;
